/**
 * Services Business Logic Hooks
 *
 * TanStack Query hooks for service management (service subcategories, etc.)
 * Optimized for caching with moderate refresh intervals
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { estimateListApi, generateEstimateNumberApi } from './estimates.api';

// Query Keys for invoices domain
export const estimateQueryKeys = {
    all: ['estimates'],
    generateEstimateNumber: () => [...estimateQueryKeys.all, 'generateEstimateNumber'],
    estimateList: (params) => [...estimateQueryKeys.all, 'list', params],
    estimateInfiniteList: (params) => [...estimateQueryKeys.all, 'infiniteList', params],
};

export const useGenerateEstimateNumber = (id, options = {}) => {
    return useQuery({
        queryKey: estimateQueryKeys.generateEstimateNumber(),
        queryFn: () => generateEstimateNumberApi(),
        enabled: !id ? true : false,
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        ...options,
    });
};

export const useEstimateList = (params, options = {}) => {
    return useQuery({
        queryKey: estimateQueryKeys.estimateList(params),
        queryFn: () => estimateListApi(params),
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        ...options,
    });
};

/**
 * Hook to get infinite estimate list with search and pagination
 *
 * Uses infinite query for optimized loading and caching:
 * - Infinite scroll pagination
 * - Smart search debouncing
 * - Optimized caching with search parameters
 * - Background refetch for better UX
 * - Automatic cache invalidation on estimate changes
 *
 * @param {object} params - Query parameters
 * @param {number} params.limit - Number of items per page (default: 20)
 * @param {string} params.status - Estimate status filter
 * @param {string} params.sortType - Sort order (ASC/DSC)
 * @param {string} params.sortBy - Sort field
 * @param {string} params.customer - Customer ID filter
 * @param {string} params.customerSearch - Customer search term
 * @param {object} options - Query options
 * @returns {object} Infinite query result with estimate data
 */
export const useInfiniteEstimateList = (params = {}, options = {}) => {
    // Set default limit if not provided
    const queryParams = {
        limit: 20,
        ...params,
    };

    return useInfiniteQuery({
        queryKey: estimateQueryKeys.estimateInfiniteList(queryParams),
        queryFn: ({ pageParam = 1 }) => {
            return estimateListApi({
                ...queryParams,
                page: pageParam,
            });
        },
        getNextPageParam: (lastPage, allPages) => {
            // Handle different API response structures
            let lastPageData = [];
            let hasMore = false;

            // If lastPage is an array directly (most likely case based on current usage)
            if (Array.isArray(lastPage)) {
                lastPageData = lastPage;
                // If the last page has items equal to the limit, there might be more pages
                hasMore = lastPage.length === queryParams.limit;
            }
            // If API returns an object with data array and pagination info
            else if (lastPage && typeof lastPage === 'object') {
                // Check for pagination metadata
                if (lastPage.totalPages !== undefined) {
                    hasMore = allPages.length < lastPage.totalPages;
                } else if (lastPage.hasMore !== undefined) {
                    hasMore = lastPage.hasMore;
                } else if (lastPage.data && Array.isArray(lastPage.data)) {
                    lastPageData = lastPage.data;
                    hasMore = lastPageData.length === queryParams.limit;
                } else if (Array.isArray(lastPage.estimates)) {
                    lastPageData = lastPage.estimates;
                    hasMore = lastPageData.length === queryParams.limit;
                }
            }

            return hasMore ? allPages.length + 1 : undefined;
        },
        getPreviousPageParam: (firstPage, allPages) => {
            // For previous page support (optional)
            if (allPages.length > 1) {
                return allPages.length - 1;
            }
            return undefined;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        refetchOnWindowFocus: false, // Prevent refetch on window focus for better performance
        ...options,
    });
};
